/**
 * LOS CLIENTES GUARDADOS EN EL APARATO, PUESTOS AL DÍA.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  LO QUE PASÓ (2026-10-19)
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * En una capacitación se importaron clientes y la persona que ya estaba dentro
 * no los encontraba: tuvo que cerrar sesión y volver a entrar. El selector de
 * clientes busca en la copia del aparato (para poder buscar sin señal), y esa
 * copia solo se renovaba al abrir la Caja. Lo que entraba después —un Excel
 * importado, un cliente que registró otra caja— no llegaba hasta volver a
 * abrirla, y en Cotizaciones y Cuentas por cobrar, que usan el mismo selector,
 * ni eso.
 *
 * Ahora se pone al día también **al abrir el selector**, que es cuando de
 * verdad se necesita. Es barato: va por huellas (ver `porHuellas.ts`), así que
 * si nada cambió solo viajan las 256 huellas, y si entraron 300 clientes solo
 * bajan sus grupos. Cuando termina, el selector abierto se repinta solo.
 *
 * ⚠️ Una sola descarga a la vez: abrir el selector mientras la Caja todavía
 * baja los clientes espera a esa misma descarga en vez de lanzar otra.
 */

import { clientesGuardados, guardarClientes, guardarHuellas, leerHuellas } from "@/lib/db";
import { invalidarConsultas } from "@/lib/cambioDeNegocio";
import { clientes as clientesApi } from "@/lib/endpoints";
import type { Customer } from "@/lib/types";
import { useAuth } from "@/stores/auth";
import { descargarPaginadoConTope } from "./descargarPaginado";
import { renovarPorHuellas, type ResultadoPorHuellas } from "./porHuellas";

/**
 * Los clientes que se guardan en el dispositivo: TODOS.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ LOS CLIENTES SE BAJAN ENTEROS, IGUAL QUE EL CATÁLOGO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Lo pidió el dueño después de usar Softfi sin internet: «hay que hacer que
 * carguen todos los clientes cargados». Y no es comodidad — con la
 * configuración de cliente obligatorio, sin la lista NO SE PUEDE COBRAR: el
 * buscador salía vacío, había que registrar a alguien que ya existía, y a la
 * segunda venta el mismo señor tenía dos fichas.
 *
 * ⚠️ SIN TOPE, y también lo pidió él. Había uno de cinco mil, y los que no
 * cabían —los últimos por orden alfabético, así que la Zulay que debía plata—
 * no se encontraban sin internet: el cajero los volvía a registrar. Un cliente
 * es un nombre, un teléfono y una cédula; cincuenta mil son unos pocos megas.
 * Por eso bajan por su lado y no frenan la caja (ver `cargar` en `useDatosPos`).
 */
const SIN_TOPE = Number.POSITIVE_INFINITY;
const CLIENTES_POR_PAGINA = 200;

/**
 * Desde el selector no se pregunta más de una vez cada tanto: quien abre y
 * cierra el selector tres veces en una venta no necesita tres revisiones.
 *
 * ⚠️ Solo cuentan las revisiones del selector, no la de la Caja al abrirse: si
 * la Caja bajó los clientes y justo después se importó un Excel, abrir el
 * selector tiene que traerlos aunque haya pasado un segundo.
 */
const ESPERA_ENTRE_REVISIONES = 10_000;

/**
 * Los clientes del negocio, todos.
 *
 * ⚠️ Se piden SIN texto de búsqueda: es la lista completa, que es lo que hay
 * que tener guardado para poder buscar sin señal. El servidor ya deja fuera
 * los inactivos y las financieras —Cashea no es un cliente al que venderle—,
 * así que lo que llega es exactamente lo que el selector debe enseñar.
 */
async function descargarClientes(tenantId: string): Promise<ResultadoPorHuellas<Customer>> {
  const pedirPagina = (pagina: number, grupos?: string) =>
    clientesApi.buscar({ ...(grupos ? { grupos } : {}), page: pagina, size: CLIENTES_POR_PAGINA });
  try {
    // Solo lo que cambió, igual que el catálogo. Ver `porHuellas.ts`.
    return await renovarPorHuellas<Customer>({
      pedirHuellas: clientesApi.huellas,
      pedirPagina,
      locales: await clientesGuardados(),
      guardadas: await leerHuellas("clientes"),
      clave: `v1|${tenantId}`,
      porPagina: CLIENTES_POR_PAGINA,
      tope: SIN_TOPE,
    });
  } catch {
    const { items: filas } = await descargarPaginadoConTope((pagina) => pedirPagina(pagina), {
      porPagina: CLIENTES_POR_PAGINA,
      tope: SIN_TOPE,
    });
    return { filas, huellas: null, recortado: false, bajadas: filas.length };
  }
}

let enCurso: Promise<void> | null = null;
let ultimaDesdeElSelector = 0;

/**
 * Baja lo que cambió, lo guarda y repinta los selectores abiertos.
 *
 * Nunca lanza: sin señal, o sin permiso para ver clientes, la copia que ya
 * hubiera se queda intacta.
 */
export function ponerAlDiaLosClientes(tenantId: string): Promise<void> {
  if (enCurso) return enCurso;

  enCurso = (async () => {
    try {
      const renovado = await descargarClientes(tenantId);
      // Si mientras bajaban se cambió de negocio, esto ya es de otro: la copia
      // del aparato se vació al cambiar y no se le mete nada del anterior.
      if (useAuth.getState().tenant?.id !== tenantId) return;
      await guardarClientes(renovado.filas);
      // Las huellas, DESPUÉS de guardar lo que dicen: si algo falla a medias,
      // la próxima vez no coinciden y se vuelve a bajar lo que faltó.
      await guardarHuellas("clientes", renovado.huellas);
      // El selector lee la copia con la clave `["clientes", "del-dispositivo"]`.
      invalidarConsultas(["clientes", "del-dispositivo"]);
    } catch {
      // Sin señal o sin permiso: se sigue con lo que había.
    } finally {
      enCurso = null;
    }
  })();
  return enCurso;
}

/**
 * Desde el selector de clientes: al abrirlo, y cuando una búsqueda no encuentra
 * a nadie (puede ser alguien que entró hace un momento). Revisa salvo que el
 * selector ya lo haya hecho hace nada.
 */
export function revisarClientesDesdeElSelector(): void {
  const tenantId = useAuth.getState().tenant?.id;
  if (!tenantId || !navigator.onLine) return;
  if (Date.now() - ultimaDesdeElSelector < ESPERA_ENTRE_REVISIONES) return;
  ultimaDesdeElSelector = Date.now();
  void ponerAlDiaLosClientes(tenantId);
}
